import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useETSFRecords, type ETSFChecklistItem } from '../../../hooks/useETSFRecords'
import { useETSFDaily } from '../../../hooks/useETSFDaily'
import { useETSFTraining, isExpired, isExpiringSoon } from '../../../hooks/useETSFTraining'
import { useETSFIncidents } from '../../../hooks/useETSFIncidents'
import { useAuth } from '../../../contexts/AuthContext'
import SOPView from './SOPView'
import IncidentsTab from './IncidentsTab'
import ETSFComplianceStatusCard from '../../../components/ETSFComplianceStatusCard'

const INPUT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
const LABEL_CLS = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'

const DEFAULT_CHECKLISTS = {
  hmrc_inspection: [
    { category: 'Self-Collection Controls', item_description: 'Self-collection log maintained and up to date' },
    { category: 'Self-Collection Controls', item_description: 'Management approval obtained for all releases' },
    { category: 'Self-Collection Controls', item_description: 'Self-collection policy suspended (no resumption without HMRC approval)' },
    { category: 'Self-Collection Controls', item_description: 'Dual verification completed for all releases' },
    { category: 'Vehicle Verification', item_description: 'Vehicle registration recorded for all collections' },
    { category: 'Vehicle Verification', item_description: 'Driver identity verified and documented' },
    { category: 'Vehicle Verification', item_description: 'Vehicle access log maintained' },
    { category: 'Documentation', item_description: 'All MRN/entry documents verified against physical stock' },
    { category: 'Documentation', item_description: 'Release documentation signed by supervisor' },
    { category: 'Documentation', item_description: 'Collection logs complete and accurate' },
    { category: 'Documentation', item_description: 'At-risk consignments identified and isolated' },
    { category: 'CCTV & Security', item_description: 'CCTV fully operational' },
    { category: 'CCTV & Security', item_description: 'CCTV footage accessible and preserved' },
    { category: 'CCTV & Security', item_description: 'Recording coverage adequate for all access points' },
    { category: 'CCTV & Security', item_description: 'Access control logs maintained' },
    { category: 'Staff Compliance', item_description: 'Staff emergency compliance briefing completed' },
    { category: 'Staff Compliance', item_description: 'Staff training records current' },
    { category: 'Staff Compliance', item_description: 'SOPs available and understood by all staff' },
  ],
  facility_check: [
    { category: 'Goods Control', item_description: 'Physical stock matches system records' },
    { category: 'Goods Control', item_description: 'At-risk consignments secured and segregated' },
    { category: 'Goods Control', item_description: 'No unaccounted goods present' },
    { category: 'Goods Control', item_description: 'All goods properly labelled' },
    { category: 'Security Infrastructure', item_description: 'CCTV functioning and recording' },
    { category: 'Security Infrastructure', item_description: 'Access control operational' },
    { category: 'Security Infrastructure', item_description: 'Restricted areas properly secured' },
    { category: 'Security Infrastructure', item_description: 'Fire safety equipment checked' },
    { category: 'Documentation & Records', item_description: 'Daily stock reconciliation completed' },
    { category: 'Documentation & Records', item_description: 'Arrival records updated within 24 hours' },
    { category: 'Documentation & Records', item_description: 'Departure records include customs reference' },
  ],
  self_audit: [
    { category: 'CAPA Action Points', item_description: 'All immediate corrective actions verified complete' },
    { category: 'CAPA Action Points', item_description: 'Dual verification process in place and followed' },
    { category: 'CAPA Action Points', item_description: 'Mandatory collection logs in use' },
    { category: 'CAPA Action Points', item_description: 'Supervisor sign-off procedure followed for all releases' },
    { category: 'CAPA Action Points', item_description: 'Previous audit action points resolved' },
    { category: 'Monthly Compliance', item_description: 'Monthly stock reconciliation completed' },
    { category: 'Monthly Compliance', item_description: 'Weekly internal compliance audit conducted' },
    { category: 'Monthly Compliance', item_description: 'Risk register reviewed and updated' },
    { category: 'Monthly Compliance', item_description: 'Procedure manual up to date' },
  ],
  weekly_audit: [
    { category: 'Management Paperwork Checklist', item_description: 'Every Release Note: signed, time stamped and completed correctly' },
    { category: 'Management Paperwork Checklist', item_description: 'Airfreight Vehicle Log: all fields filled, complete, and page marked' },
    { category: 'Management Paperwork Checklist', item_description: 'Seafreight Vehicle Log: all fields filled, complete, and page marked' },
    { category: 'Management Paperwork Checklist', item_description: 'Road Movements Vehicle Log: all fields filled, complete, and page marked' },
    { category: 'Management Paperwork Checklist', item_description: 'Release Notes: weekly scanned and saved in computer' },
    { category: 'Management Paperwork Checklist', item_description: 'Randomly pick collection note/delivery note to verify requirements are met' },
    { category: 'Management Paperwork Checklist', item_description: 'Bond Check record: recorded timely and clearly' },
  ],
}

// Z64: Mid-week Checklist compliance is "once per Mon-Fri week" now, not "once per day" --
// this Mon..Fri (inclusive) window, as YYYY-MM-DD strings, matches useETSFComplianceStatus.
function thisWeekMonToFri(): [string, string] {
  const now = new Date()
  const dayOfWeek = now.getDay() // 0=Sun..6=Sat
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromMonday)
  const friday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 4)
  return [monday.toISOString().split('T')[0], friday.toISOString().split('T')[0]]
}

interface FormData {
  location: string
  record_type: string
  record_date: string
  // Z64.2: which job was actually checked -- required only for weekly_audit (a random
  // collection/delivery note pick), optional for the whole-facility record types.
  job_ref: string
  inspector_name: string
  conducted_by: string
  notes: string
  action_required: string
  action_due_date: string
}

export default function ETSFCompliance() {
  const navigate = useNavigate()
  const { userEmail } = useAuth()
  const [activeFeatureTab, setActiveFeatureTab] = useState<'Records' | 'Mid-week Checklist' | 'Weekly Audit' | 'Training Records' | 'Incidents' | 'SOP'>('Records')
  const [activeLocation, setActiveLocation] = useState<'BHX' | 'LHR'>('BHX')
  const { records, stats, loading, error, createRecord, createChecklistItems } = useETSFRecords(activeLocation)
  const { checklists, loading: dailyLoading, error: dailyError } = useETSFDaily(activeLocation)
  const { training, loading: trainingLoading, error: trainingError, createTraining } = useETSFTraining()
  const { getStats: getIncidentStats } = useETSFIncidents()

  const [showForm, setShowForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [checklist, setChecklist] = useState<ETSFChecklistItem[]>([])
  const [showTrainingForm, setShowTrainingForm] = useState(false)
  const [trainingFormError, setTrainingFormError] = useState<string | null>(null)
  const [submittingTraining, setSubmittingTraining] = useState(false)
  const [trainingData, setTrainingData] = useState({
    staff_name: '',
    role: '',
    training_type: 'SOP_awareness',
    training_date: new Date().toISOString().split('T')[0],
    trainer_provider: '',
    expiry_date: '',
    notes: '',
  })

  const [formData, setFormData] = useState<FormData>({
    location: activeLocation,
    record_type: 'hmrc_inspection',
    record_date: new Date().toISOString().split('T')[0],
    job_ref: '',
    inspector_name: '',
    conducted_by: '',
    notes: '',
    action_required: '',
    action_due_date: '',
  })

  // Update location in form when tab changes
  useEffect(() => {
    setFormData(f => ({ ...f, location: activeLocation }))
  }, [activeLocation])

  // Update checklist when record type changes
  useEffect(() => {
    const defaultItems = DEFAULT_CHECKLISTS[formData.record_type as keyof typeof DEFAULT_CHECKLISTS] || []
    setChecklist(
      defaultItems.map(item => ({
        id: Math.random().toString(),
        record_id: '',
        ...item,
        checked: false,
        result: null,
        notes: '',
      }))
    )
  }, [formData.record_type])

  const calculateResult = (): string => {
    if (checklist.some(i => i.result === 'fail')) return 'fail'
    if (checklist.some(i => i.result === 'advisory')) return 'advisory'
    return 'pass'
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setFormError(null)

    try {
      if (formData.record_type === 'weekly_audit' && !formData.job_ref.trim()) {
        throw new Error('Job Ref is required for a Weekly Audit')
      }

      const result = calculateResult()
      const recordData = {
        location: formData.location,
        record_type: formData.record_type,
        record_date: formData.record_date,
        job_ref: formData.job_ref.trim() || null,
        inspector_name: formData.inspector_name || null,
        conducted_by: formData.conducted_by || null,
        result,
        notes: formData.notes || null,
        action_required: formData.action_required || null,
        action_due_date: formData.action_due_date || null,
        created_by: userEmail,
      }

      const created = await createRecord(recordData)

      // Create checklist items
      await createChecklistItems(
        created.id,
        checklist.map(item => ({
          category: item.category,
          item_description: item.item_description,
          result: item.result,
          notes: item.notes,
        }))
      )

      // Reset form
      setShowForm(false)
      setFormData({
        location: activeLocation,
        record_type: 'hmrc_inspection',
        record_date: new Date().toISOString().split('T')[0],
        job_ref: '',
        inspector_name: '',
        conducted_by: '',
        notes: '',
        action_required: '',
        action_due_date: '',
      })
      setChecklist([])
    } catch (err: any) {
      setFormError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleChecklistChange = (index: number, key: string, value: any) => {
    const updated = [...checklist]
    updated[index] = { ...updated[index], [key]: value }
    setChecklist(updated)
  }

  const handleTrainingSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmittingTraining(true)
    setTrainingFormError(null)

    try {
      await createTraining({
        staff_name: trainingData.staff_name,
        role: trainingData.role || null,
        training_type: trainingData.training_type,
        training_date: trainingData.training_date,
        trainer_provider: trainingData.trainer_provider || null,
        expiry_date: trainingData.expiry_date || null,
        notes: trainingData.notes || null,
        created_by: userEmail,
      })

      setTrainingData({
        staff_name: '',
        role: '',
        training_type: 'SOP_awareness',
        training_date: new Date().toISOString().split('T')[0],
        trainer_provider: '',
        expiry_date: '',
        notes: '',
      })
      setShowTrainingForm(false)
    } catch (err: any) {
      setTrainingFormError(err.message)
    } finally {
      setSubmittingTraining(false)
    }
  }

  if (loading || dailyLoading || trainingLoading) return <div className="p-6">Loading...</div>
  if (error || dailyError || trainingError) return <div className="p-6 text-red-600">{error || dailyError || trainingError}</div>

  const filteredRecords = records.filter(r => r.location === activeLocation)
  const filteredChecklists = checklists.filter(c => c.location === activeLocation)
  const currentResult = calculateResult()

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
          <button
            onClick={() => navigate('/customs')}
            className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            Customs
          </button>
          <span>/</span>
          <span className="text-gray-900 dark:text-white">ETSF Compliance</span>
        </div>

        <h1 className="text-2xl font-bold mb-6">ETSF Compliance</h1>

        {/* ETSF Compliance Status Alerts */}
        <ETSFComplianceStatusCard />

        {/* Feature Tabs */}
        <div className="flex border-b mb-6 bg-white dark:bg-gray-800 rounded-t-lg overflow-x-auto">
          {['Records', 'Mid-week Checklist', 'Weekly Audit', 'Training Records', 'Incidents', 'SOP'].map(tab => {
            const incidentStats = getIncidentStats()
            const showBadge = tab === 'Incidents' && (incidentStats.criticalCount > 0 || incidentStats.highCount > 0)
            return (
              <button
                key={tab}
                className={`px-6 py-3 font-medium border-b-2 transition whitespace-nowrap relative ${
                  activeFeatureTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-400'
                }`}
                onClick={() => setActiveFeatureTab(tab as any)}
              >
                {tab}
                {showBadge && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-600 rounded-full"></span>
                )}
              </button>
            )
          })}
        </div>

        {/* Location Tabs (Records, Mid-week Checklist, Incidents, SOP) */}
        {(activeFeatureTab === 'Records' || activeFeatureTab === 'Mid-week Checklist' || activeFeatureTab === 'Incidents' || activeFeatureTab === 'SOP') && (
          <div className="flex border-b mb-6 bg-white dark:bg-gray-800 rounded-t-lg">
            {[
              { value: 'BHX', label: activeFeatureTab === 'SOP' ? 'BHX — Birmingham' : 'BHX' },
              { value: 'LHR', label: activeFeatureTab === 'SOP' ? 'LHR — Slough' : 'LHR' },
            ].map(loc => (
              <button
                key={loc.value}
                className={`px-6 py-3 font-medium border-b-2 transition ${
                  activeLocation === loc.value
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-400'
                }`}
                onClick={() => setActiveLocation(loc.value as 'BHX' | 'LHR')}
              >
                {loc.label}
              </button>
            ))}
          </div>
        )}

        {/* RECORDS TAB */}
        {activeFeatureTab === 'Records' && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Records</h2>
              <button
                onClick={() => setShowForm(!showForm)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {showForm ? 'Cancel' : 'New Record'}
              </button>
            </div>


        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">This Year Records</div>
              <div className="text-2xl font-bold mt-1">{stats.total_this_year}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">Last HMRC Inspection</div>
              <div className="text-lg font-semibold mt-1">
                {stats.last_hmrc_inspection_date ? new Date(stats.last_hmrc_inspection_date).toLocaleDateString('en-GB') : '-'}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">Last Self-Audit</div>
              <div className="text-lg font-semibold mt-1">
                {stats.last_self_audit_date ? new Date(stats.last_self_audit_date).toLocaleDateString('en-GB') : '-'}
              </div>
            </div>
          </div>
        )}

        {/* New Record Form */}
        {showForm && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">New Record</h2>
            {formError && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{formError}</div>}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className={LABEL_CLS}>Location</label>
                  <select
                    className={INPUT_CLS}
                    value={formData.location}
                    onChange={e => setFormData(f => ({ ...f, location: e.target.value }))}
                  >
                    <option value="BHX">BHX</option>
                    <option value="LHR">LHR</option>
                  </select>
                </div>

                <div>
                  <label className={LABEL_CLS}>Record Type</label>
                  <select
                    className={INPUT_CLS}
                    value={formData.record_type}
                    onChange={e => setFormData(f => ({ ...f, record_type: e.target.value }))}
                  >
                    <option value="hmrc_inspection">HMRC Inspection</option>
                    <option value="facility_check">Facility Check</option>
                    <option value="self_audit">Self Audit</option>
                    <option value="weekly_audit">Weekly Audit</option>
                  </select>
                </div>

                <div>
                  <label className={LABEL_CLS}>Record Date</label>
                  <input
                    type="date"
                    className={INPUT_CLS}
                    value={formData.record_date}
                    onChange={e => setFormData(f => ({ ...f, record_date: e.target.value }))}
                  />
                </div>

                <div>
                  <label className={LABEL_CLS}>Conducted By</label>
                  <input
                    type="text"
                    className={INPUT_CLS}
                    value={formData.conducted_by}
                    onChange={e => setFormData(f => ({ ...f, conducted_by: e.target.value }))}
                    placeholder="Name"
                  />
                </div>

                {formData.record_type === 'hmrc_inspection' && (
                  <div>
                    <label className={LABEL_CLS}>Inspector Name</label>
                    <input
                      type="text"
                      className={INPUT_CLS}
                      value={formData.inspector_name}
                      onChange={e => setFormData(f => ({ ...f, inspector_name: e.target.value }))}
                      placeholder="Inspector name"
                    />
                  </div>
                )}

                {formData.record_type === 'weekly_audit' && (
                  <div>
                    <label className={LABEL_CLS}>Job Ref</label>
                    <input
                      type="text"
                      className={INPUT_CLS}
                      value={formData.job_ref}
                      onChange={e => setFormData(f => ({ ...f, job_ref: e.target.value.toUpperCase() }))}
                      placeholder="Which job's collection/delivery note is being checked, e.g. K10251D"
                      required
                    />
                  </div>
                )}

                <div className="col-span-2">
                  <label className={LABEL_CLS}>Notes</label>
                  <textarea
                    className={INPUT_CLS}
                    rows={2}
                    value={formData.notes}
                    onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Any notes..."
                  />
                </div>

                <div className="col-span-2">
                  <label className={LABEL_CLS}>Action Required</label>
                  <textarea
                    className={INPUT_CLS}
                    rows={2}
                    value={formData.action_required}
                    onChange={e => setFormData(f => ({ ...f, action_required: e.target.value }))}
                    placeholder="Any action required..."
                  />
                </div>

                <div>
                  <label className={LABEL_CLS}>Action Due Date</label>
                  <input
                    type="date"
                    className={INPUT_CLS}
                    value={formData.action_due_date}
                    onChange={e => setFormData(f => ({ ...f, action_due_date: e.target.value }))}
                  />
                </div>
              </div>

              {/* Checklist Section */}
              <div className="mb-6">
                <h3 className="font-semibold mb-4 text-gray-700 dark:text-gray-300">Checklist Items</h3>

                {checklist.length > 0 && (
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                          <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-medium">Item</th>
                          <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-medium">Result</th>
                          <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-medium">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checklist.map((item, idx) => (
                          <tr key={idx} className="border-b dark:border-gray-700">
                            <td className="px-3 py-2">{item.item_description}</td>
                            <td className="px-3 py-2">
                              <select
                                className={INPUT_CLS}
                                value={item.result || 'na'}
                                onChange={e => handleChecklistChange(idx, 'result', e.target.value)}
                              >
                                <option value="na">N/A</option>
                                <option value="pass">Pass</option>
                                <option value="fail">Fail</option>
                              </select>
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                className={INPUT_CLS}
                                value={item.notes || ''}
                                onChange={e => handleChecklistChange(idx, 'notes', e.target.value)}
                                placeholder="Notes..."
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Result Banner */}
                <div
                  className={`p-3 rounded text-sm font-medium mb-4 ${
                    currentResult === 'fail'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      : currentResult === 'advisory'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  }`}
                >
                  Calculated Result: {currentResult.toUpperCase()}
                </div>
              </div>

              <div className="flex gap-3 justify-end border-t border-gray-200 dark:border-gray-700 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : 'Save Record'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Records List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Date</th>
                <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Type</th>
                <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Conducted By</th>
                <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Inspector</th>
                <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Result</th>
                <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length > 0 ? (
                filteredRecords.map(record => {
                  const borderColor =
                    record.result === 'fail'
                      ? 'border-l-4 border-l-red-500 bg-red-50 dark:bg-red-900/20'
                      : record.result === 'advisory'
                        ? 'border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-900/20'
                        : 'border-l-4 border-l-green-500 bg-green-50 dark:bg-green-900/20'

                  const resultColor =
                    record.result === 'fail'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      : record.result === 'advisory'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'

                  return (
                    <tr key={record.id} className={`border-b dark:border-gray-700 ${borderColor}`}>
                      <td className="px-4 py-3">{new Date(record.record_date).toLocaleDateString('en-GB')}</td>
                      <td className="px-4 py-3 capitalize">{record.record_type.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3">{record.conducted_by || '-'}</td>
                      <td className="px-4 py-3">{record.inspector_name || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${resultColor}`}>
                          {record.result || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/customs/etsf/${record.id}`} className="text-blue-600 hover:underline">
                          View
                        </Link>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No records for {activeLocation}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
          </>
        )}

        {/* MID-WEEK CHECKLIST TAB */}
        {activeFeatureTab === 'Mid-week Checklist' && (
          <>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-semibold">Mid-week Checklists</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Location: {activeLocation}</p>
              </div>
              <button
                onClick={() => navigate(`/customs/etsf/checklist/new?loc=${activeLocation}`)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                New Mid-week Checklist
              </button>
            </div>

            {/* Mid-week Checklist Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">This Week's Submissions</div>
                <div className="text-2xl font-bold mt-1">
                  {(() => {
                    const [weekStart, weekEnd] = thisWeekMonToFri()
                    return filteredChecklists.filter(c => c.checklist_date >= weekStart && c.checklist_date <= weekEnd).length
                  })()}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">This Week Pass Rate</div>
                <div className="text-2xl font-bold mt-1">
                  {filteredChecklists.length > 0
                    ? `${Math.round((filteredChecklists.filter(c => c.overall_result === 'pass').length / filteredChecklists.length) * 100)}%`
                    : '-'}
                </div>
              </div>
            </div>

            {/* Mid-week Checklists List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Date</th>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Job Ref</th>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Completed By</th>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Result</th>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredChecklists.length > 0 ? (
                    filteredChecklists.map(checklist => {
                      const borderColor =
                        checklist.overall_result === 'fail'
                          ? 'border-l-4 border-l-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-l-4 border-l-green-500 bg-green-50 dark:bg-green-900/20'
                      const resultColor =
                        checklist.overall_result === 'fail'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      return (
                        <tr key={checklist.id} className={`border-b dark:border-gray-700 ${borderColor}`}>
                          <td className="px-4 py-3">{new Date(checklist.checklist_date).toLocaleDateString('en-GB')}</td>
                          <td className="px-4 py-3 font-mono">{checklist.job_ref || '—'}</td>
                          <td className="px-4 py-3">{checklist.completed_by}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${resultColor}`}>
                              {checklist.overall_result || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <Link to={`/customs/etsf/checklist/${checklist.id}`} className="text-blue-600 hover:underline">
                              View
                            </Link>
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        No mid-week checklists for {activeLocation}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* WEEKLY AUDIT TAB */}
        {activeFeatureTab === 'Weekly Audit' && (
          <>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-semibold">Weekly Audits</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Location: {activeLocation}</p>
              </div>
              <button
                onClick={() => {
                  setFormData(f => ({ ...f, record_type: 'weekly_audit', record_date: new Date().toISOString().split('T')[0] }))
                  setActiveFeatureTab('Records')
                  setShowForm(true)
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                New Weekly Audit
              </button>
            </div>

            {/* Weekly Audits List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Date</th>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Job Ref</th>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Conducted By</th>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Result</th>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.filter(r => r.record_type === 'weekly_audit' && r.location === activeLocation).length > 0 ? (
                    records.filter(r => r.record_type === 'weekly_audit' && r.location === activeLocation).map(record => {
                      const borderColor =
                        record.result === 'fail'
                          ? 'border-l-4 border-l-red-500 bg-red-50 dark:bg-red-900/20'
                          : record.result === 'advisory'
                            ? 'border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-900/20'
                            : 'border-l-4 border-l-green-500 bg-green-50 dark:bg-green-900/20'
                      const resultColor =
                        record.result === 'fail'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : record.result === 'advisory'
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                            : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      return (
                        <tr key={record.id} className={`border-b dark:border-gray-700 ${borderColor}`}>
                          <td className="px-4 py-3">{new Date(record.record_date).toLocaleDateString('en-GB')}</td>
                          <td className="px-4 py-3 font-mono">{record.job_ref || '—'}</td>
                          <td className="px-4 py-3">{record.conducted_by || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${resultColor}`}>
                              {record.result || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <Link to={`/customs/etsf/${record.id}`} className="text-blue-600 hover:underline">
                              View
                            </Link>
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        No weekly audits for {activeLocation}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* TRAINING RECORDS TAB */}
        {activeFeatureTab === 'Training Records' && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Training Records</h2>
              <button
                onClick={() => setShowTrainingForm(!showTrainingForm)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {showTrainingForm ? 'Cancel' : 'Add Training Record'}
              </button>
            </div>

            {/* Training Form */}
            {showTrainingForm && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">New Training Record</h3>
                {trainingFormError && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{trainingFormError}</div>}

                <form onSubmit={handleTrainingSubmit}>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className={LABEL_CLS}>Staff Name</label>
                      <input
                        type="text"
                        className={INPUT_CLS}
                        value={trainingData.staff_name}
                        onChange={e => setTrainingData(f => ({ ...f, staff_name: e.target.value }))}
                        placeholder="Full name"
                        required
                      />
                    </div>

                    <div>
                      <label className={LABEL_CLS}>Role</label>
                      <input
                        type="text"
                        className={INPUT_CLS}
                        value={trainingData.role}
                        onChange={e => setTrainingData(f => ({ ...f, role: e.target.value }))}
                        placeholder="e.g. Warehouse Manager"
                      />
                    </div>

                    <div>
                      <label className={LABEL_CLS}>Training Type</label>
                      <select
                        className={INPUT_CLS}
                        value={trainingData.training_type}
                        onChange={e => setTrainingData(f => ({ ...f, training_type: e.target.value }))}
                      >
                        <option value="SOP_awareness">SOP Awareness</option>
                        <option value="customs_compliance">Customs Compliance</option>
                        <option value="security_procedure">Security Procedure</option>
                        <option value="emergency_briefing">Emergency Briefing</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className={LABEL_CLS}>Training Date</label>
                      <input
                        type="date"
                        className={INPUT_CLS}
                        value={trainingData.training_date}
                        onChange={e => setTrainingData(f => ({ ...f, training_date: e.target.value }))}
                        required
                      />
                    </div>

                    <div>
                      <label className={LABEL_CLS}>Trainer/Provider</label>
                      <input
                        type="text"
                        className={INPUT_CLS}
                        value={trainingData.trainer_provider}
                        onChange={e => setTrainingData(f => ({ ...f, trainer_provider: e.target.value }))}
                        placeholder="Name or organization"
                      />
                    </div>

                    <div>
                      <label className={LABEL_CLS}>Expiry Date</label>
                      <input
                        type="date"
                        className={INPUT_CLS}
                        value={trainingData.expiry_date}
                        onChange={e => setTrainingData(f => ({ ...f, expiry_date: e.target.value }))}
                      />
                    </div>

                    <div className="col-span-2">
                      <label className={LABEL_CLS}>Notes</label>
                      <textarea
                        className={INPUT_CLS}
                        rows={2}
                        value={trainingData.notes}
                        onChange={e => setTrainingData(f => ({ ...f, notes: e.target.value }))}
                        placeholder="Any notes..."
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 justify-end border-t border-gray-200 dark:border-gray-700 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowTrainingForm(false)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submittingTraining}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {submittingTraining ? 'Saving...' : 'Save Record'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Training Records List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Staff Name</th>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Role</th>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Type</th>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Training Date</th>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Provider</th>
                    <th className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {training.length > 0 ? (
                    training.map(record => {
                      let statusBadge = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      let statusText = '-'
                      if (record.expiry_date) {
                        if (isExpired(record)) {
                          statusBadge = 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          statusText = 'Expired'
                        } else if (isExpiringSoon(record)) {
                          statusBadge = 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                          statusText = 'Expiring Soon'
                        } else {
                          statusBadge = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          statusText = 'Valid'
                        }
                      }
                      return (
                        <tr key={record.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3">{record.staff_name}</td>
                          <td className="px-4 py-3">{record.role || '-'}</td>
                          <td className="px-4 py-3 capitalize">{record.training_type.replace(/_/g, ' ')}</td>
                          <td className="px-4 py-3">{new Date(record.training_date).toLocaleDateString('en-GB')}</td>
                          <td className="px-4 py-3">{record.trainer_provider || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${statusBadge}`}>
                              {statusText}
                            </span>
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        No training records
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* INCIDENTS TAB */}
        {activeFeatureTab === 'Incidents' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <IncidentsTab location={activeLocation} />
          </div>
        )}

        {/* SOP TAB */}
        {activeFeatureTab === 'SOP' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <SOPView location={activeLocation} />
          </div>
        )}
      </div>
    </div>
  )
}
